import { useIntl, FormattedMessage } from 'react-intl';

import { Tooltip, Divider, Button, Icon, Paragraph } from '@ets/design-system';
import { ReactNode, isValidElement } from 'react';
import cn from 'classnames';

type SettingProps = {
  style?: React.CSSProperties;
  labelStyles?: React.CSSProperties;
  label: string;
  manager?: boolean;
  children?: ReactNode;
  onEdit?: () => void;
  onEditLabel?: string;
  secondValue?: ReactNode;
  valueStyles?: React.CSSProperties;
  isOwner?: boolean;
  editable?: boolean;
  testId: string;
  className?: string;
};

const Setting = ({
  style,
  labelStyles,
  label,
  manager,
  onEdit,
  onEditLabel,
  secondValue,
  children,
  isOwner,
  editable,
  testId,
  className,
}: SettingProps) => {
  const intl = useIntl();
  const isEditDisabled =
    editable !== undefined ? !editable : typeof isOwner === 'boolean' ? !isOwner : false;

  const showEditTooltip = editable === false;
  const isText = typeof children === 'string' || typeof children === 'number';
  const hasIdProp = (element: ReactNode): element is React.ReactElement<{ id?: string }> => {
    return isValidElement(element);
  };
  const isNotSet = hasIdProp(children) && children?.props?.id === 'NOT_SET';

  return (
    <>
      <div
        className={cn('flex min-h-28 py-8', className)}
        style={style}
        data-testid={`${testId}-row-container`}
      >
        <div className="flex min-w-xl justify-start pl-16" style={labelStyles}>
          <Paragraph className="text-neutral-70" size="m">
            {label}
          </Paragraph>

          {manager && (
            <Tooltip
              content={intl.formatMessage({ id: 'OWNER_DESCRIPTION' })}
              testId={`${testId}-owner-description`}
            >
              <span>
                <Icon
                  icon="circle-info"
                  color="neutral"
                  size="2"
                  className="ml-4"
                  testId={`${testId}-owner-description`}
                />
              </span>
            </Tooltip>
          )}
        </div>

        <div
          data-testid={`${testId}-value`}
          className={cn('flex-1', !isText && isNotSet && 'text-neutral-50 italic')}
        >
          {isText ? (
            <Paragraph
              size="m"
              className="overflow-hidden text-ellipsis whitespace-normal"
              testId={`${testId}-value`}
            >
              {children}
            </Paragraph>
          ) : (
            children
          )}
        </div>

        {onEdit && (
          <Tooltip
            testId={`${testId}-cannot-edit`}
            visible={showEditTooltip}
            content={intl.formatMessage({
              id: 'CANNOT_EDIT_PROFILE_BECAUSE_YOUR_ACCOUNT_IS_CONNECTED_TO_AN_SSO',
            })}
          >
            <Button
              size="s"
              variant="link"
              icon="pen-to-square"
              onClick={onEdit}
              disabled={isEditDisabled}
              testId={`${testId}-edit`}
              className="mr-80"
            >
              <FormattedMessage id={onEditLabel} />
            </Button>
          </Tooltip>
        )}
        {secondValue && <div className="ml-9 w-84 text-sm text-neutral-50">{secondValue}</div>}
      </div>
      <Divider />
    </>
  );
};

export default Setting;
